import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  SwapHoriz as ReplaceIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CommonBreadCrumb from "components/commonBreadCrumb/CommonBreadCrumb";
import sealSignatureService from "services/SealSignatureService";
import { roleAndPermissionEnum } from "utils/rolesPermissionsEnum";
import { handlePermissions } from "utils/utils";

export default function SealSign() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);

  // URLs from server (used as fallback preview when no local file selected)
  const [sealUrl, setSealUrl] = useState("");
  const [signatureUrl, setSignatureUrl] = useState("");

  // Local files selected by user
  const [sealFile, setSealFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  // Object URLs for local file previews (cleaned up on change/unmount)
  const sealObjectUrl = useRef("");
  const signatureObjectUrl = useRef("");

  // Flags to send to API for removal
  const [removeSeal, setRemoveSeal] = useState(false);
  const [removeSignature, setRemoveSignature] = useState(false);

  // Roles & permissions for client-side disabled states
  const roleAndPermissions = useSelector(
    (state) => state?.getRolesAndPermissions?.rolesAndPermissions,
  );
  const sealSignRoleEnum = roleAndPermissionEnum.sealSign;

  const updateAccess = handlePermissions(
    roleAndPermissions,
    sealSignRoleEnum?.moduleKey,
    {
      defaultAllAccess: sealSignRoleEnum?.all,
      customAccess: sealSignRoleEnum?.update,
    },
  );

  const fetchSealSignature = async () => {
    try {
      setLoading(true);
      const res = await sealSignatureService.getInvoiceSealSignature();
      if (res?.success) {
        setSealUrl(res.data?.sealUrl || "");
        setSignatureUrl(res.data?.signatureUrl || "");
        setHasAccess(true);
      } else {
        toast.error(
          res?.message || "Failed to fetch seal & signature details.",
        );
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        setHasAccess(false);
      } else {
        toast.error(
          err?.response?.data?.message ||
            "Failed to load seal & signature details.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSealSignature();
  }, []);

  // Cleanup object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (sealObjectUrl.current) URL.revokeObjectURL(sealObjectUrl.current);
      if (signatureObjectUrl.current)
        URL.revokeObjectURL(signatureObjectUrl.current);
    };
  }, []);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, etc.).");
      return;
    }

    if (type === "seal") {
      if (sealObjectUrl.current) URL.revokeObjectURL(sealObjectUrl.current);
      sealObjectUrl.current = URL.createObjectURL(file);
      setSealFile(file);
      setRemoveSeal(false);
    } else {
      if (signatureObjectUrl.current)
        URL.revokeObjectURL(signatureObjectUrl.current);
      signatureObjectUrl.current = URL.createObjectURL(file);
      setSignatureFile(file);
      setRemoveSignature(false);
    }

    e.target.value = "";
  };

  const handleRemove = (type) => {
    if (type === "seal") {
      if (sealObjectUrl.current) {
        URL.revokeObjectURL(sealObjectUrl.current);
        sealObjectUrl.current = "";
      }
      setSealFile(null);
      setRemoveSeal(true);
    } else {
      if (signatureObjectUrl.current) {
        URL.revokeObjectURL(signatureObjectUrl.current);
        signatureObjectUrl.current = "";
      }
      setSignatureFile(null);
      setRemoveSignature(true);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const fd = new FormData();

      if (sealFile) fd.append("sealFile", sealFile);
      if (signatureFile) fd.append("signatureFile", signatureFile);
      if (removeSeal) fd.append("removeSeal", true);
      if (removeSignature) fd.append("removeSignature", true);

      const res = await sealSignatureService.updateInvoiceSealSignature(fd);
      if (res?.success) {
        toast.success(res?.message || "Seal & signature updated successfully.");
        if (sealObjectUrl.current) {
          URL.revokeObjectURL(sealObjectUrl.current);
          sealObjectUrl.current = "";
        }
        if (signatureObjectUrl.current) {
          URL.revokeObjectURL(signatureObjectUrl.current);
          signatureObjectUrl.current = "";
        }
        setSealFile(null);
        setSignatureFile(null);
        setSealUrl(res.data?.sealUrl || "");
        setSignatureUrl(res.data?.signatureUrl || "");
        setRemoveSeal(false);
        setRemoveSignature(false);
      } else {
        toast.error(res?.message || "Failed to update configuration.");
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to update configuration.");
      } else {
        toast.error(
          err?.response?.data?.message ||
            "Something went wrong while saving changes.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const sealPreview = sealFile
    ? sealObjectUrl.current
    : removeSeal
      ? ""
      : sealUrl;

  const signaturePreview = signatureFile
    ? signatureObjectUrl.current
    : removeSignature
      ? ""
      : signatureUrl;

  const hasChanges =
    !!sealFile || !!signatureFile || removeSeal || removeSignature;

  const breadcrumbPaths = [
    { name: "Configuration", url: "" },
    { name: "Seal and Sign", url: "", className: "colorBlue" },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          You do not have permission to view this page.
        </Typography>
      </Box>
    );
  }

  const UploadCard = ({ title, previewSrc, type }) => (
    <Card
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
        borderRadius: "12px",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>

      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          border: "2px dashed #ccc",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fcfcfc",
          p: 2,
          "&:hover": { borderColor: "#2196f3" },
        }}
      >
        {previewSrc ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={previewSrc}
              alt={`${title} Preview`}
              sx={{
                maxHeight: 120,
                maxWidth: "100%",
                objectFit: "contain",
                mb: 2,
              }}
            />
            {updateAccess && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<ReplaceIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Replace
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleFileChange(e, type)}
                  />
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleRemove(type)}
                  sx={{ textTransform: "none" }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 1 }} />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {type === "seal"
                ? "No seal uploaded yet"
                : "No signature uploaded yet"}
            </Typography>
            {updateAccess && (
              <Button
                variant="contained"
                component="label"
                size="small"
                sx={{ textTransform: "none" }}
              >
                {type === "seal" ? "Upload Seal" : "Upload Signature"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFileChange(e, type)}
                />
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );

  return (
    <Box sx={{ p: 3, position: "relative", minHeight: "80vh" }}>
      <CommonBreadCrumb paths={breadcrumbPaths} />

      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#111" }}>
          Invoice Seal &amp; Signature
        </Typography>
        <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
          Configure the global ITER default seal and signature images used on
          invoices and duty slips. Companies can override these with their own
          images.
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ maxWidth: 900 }}>
        <Grid item xs={12} md={6}>
          <UploadCard
            title="Invoice Seal"
            previewSrc={sealPreview}
            type="seal"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <UploadCard
            title="Invoice Signature"
            previewSrc={signaturePreview}
            type="signature"
          />
        </Grid>

        {updateAccess && (
          <Grid item xs={12}>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={saving || !hasChanges}
                onClick={handleSave}
                sx={{
                  px: 4,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              >
                {saving ? "Saving Changes..." : "Save Changes"}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
    </Box>
  );
}
